/**
 * TeamSystem Impact - Compliance Path · Phase Step
 *
 * Singola tappa lungo la "spina" del respiro: pallino + linea di collegamento a
 * sinistra, card cliccabile a destra con icona, stato, percentuale, chip chiave.
 * Al clic apre/chiude il pannello di drill-down (children) sotto la card.
 */

import type { ReactNode } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  LinearProgress,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { PhaseState } from './phaseModel';

interface PhaseStepProps {
  icon: ReactNode;
  title: string;
  description: string;
  /** null = tappa "in arrivo" senza stato/percentuale (Reporting) */
  state: PhaseState | null;
  percent?: number;
  /** se valorizzato, mostra una barra per ciascun elemento (es. Compilati/Validati) */
  bars?: { label: string; percent: number }[];
  chips?: string[];
  color: string;
  isLast?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  /** se false la card non è cliccabile (es. Reporting) */
  interactive?: boolean;
  children?: ReactNode;
}

const STATE_COLOR: Record<PhaseState, 'default' | 'warning' | 'success'> = {
  not_started: 'default',
  in_progress: 'warning',
  completed: 'success',
};

const STATE_LABEL: Record<PhaseState, string> = {
  not_started: 'dashboard.path.statusNotStarted',
  in_progress: 'dashboard.path.statusInProgress',
  completed: 'dashboard.path.statusCompleted',
};

export const PhaseStep = ({
  icon,
  title,
  description,
  state,
  percent,
  bars,
  chips = [],
  color,
  isLast = false,
  expanded = false,
  onToggle,
  interactive = true,
  children,
}: PhaseStepProps) => {
  const { t } = useTranslation();

  const renderBar = (label: string, value: number) => (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" fontWeight={600}>
          {value}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: `${color}20`,
          '& .MuiLinearProgress-bar': { backgroundColor: color },
        }}
      />
    </Box>
  );

  const cardInner = (
    <CardContent>
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 2,
            backgroundColor: `${color}15`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box flexGrow={1} minWidth={0}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
            {state ? (
              <Chip size="small" color={STATE_COLOR[state]} label={t(STATE_LABEL[state])} />
            ) : (
              <Chip size="small" label={t('dashboard.path.comingSoon')} />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {description}
          </Typography>

          {bars && bars.length > 0 ? (
            <Box display="flex" flexDirection="column" gap={1} sx={{ mt: 1, mb: chips.length ? 1.5 : 0 }}>
              {bars.map((b) => (
                <Box key={b.label}>{renderBar(b.label, b.percent)}</Box>
              ))}
            </Box>
          ) : (
            state && percent !== undefined && (
              <Box sx={{ mt: 1, mb: chips.length ? 1.5 : 0 }}>
                {renderBar(t('dashboard.completamento'), percent)}
              </Box>
            )
          )}

          {chips.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
              {chips.map((c) => (
                <Chip key={c} size="small" variant="outlined" label={c} />
              ))}
            </Box>
          )}
        </Box>

        {interactive && (
          <ExpandMoreIcon
            sx={{
              color: 'text.secondary',
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'none',
            }}
          />
        )}
      </Box>
    </CardContent>
  );

  return (
    <Box display="flex" gap={2}>
      {/* Spina: pallino + linea */}
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ pt: 2.5 }}>
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: color,
            boxShadow: `0 0 0 4px ${color}22`,
            flexShrink: 0,
          }}
        />
        {!isLast && (
          <Box sx={{ width: 2, flexGrow: 1, backgroundColor: 'divider', mt: 0.5, minHeight: 24 }} />
        )}
      </Box>

      {/* Card della tappa */}
      <Box flexGrow={1} sx={{ pb: isLast ? 0 : 2.5 }}>
        <Card
          variant="outlined"
          sx={{
            borderColor: expanded ? color : 'divider',
            backgroundColor: 'rgba(255, 255, 255, 0.68)',
            backdropFilter: 'blur(3px)',
          }}
        >
          {interactive ? (
            <CardActionArea
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={title}
            >
              {cardInner}
            </CardActionArea>
          ) : (
            cardInner
          )}
          {interactive && children && (
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2, bgcolor: 'rgba(255, 255, 255, 0.35)' }}>
                {children}
              </Box>
            </Collapse>
          )}
        </Card>
      </Box>
    </Box>
  );
};
